package com.devsusana.portfolio.i18n

import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember

data class MilestoneStrings(
    val role: String,
    val company: String,
    val period: String,
    val summary: String,
    val highlights: List<String>,
    val technologies: List<String>
)

data class BlogPostStrings(
    val title: String,
    val description: String,
    val date: String,
    val readingTime: String,
    val tags: List<String>,
    val slug: String = ""
)

data class AppStrings(
    // Global & Nav
    val navRole: String,
    val navHome: String,
    val navHomeTutorPro: String,
    val navAgentic: String,
    val navExperience: String,
    val navBlog: String,
    val languageToggleLabel: String,
    val languageToggleTooltip: String,

    // Hero Section
    val heroTitle: String,
    val heroTagline: String,
    val heroSubtitle: String,
    val heroCtaHomeTutor: String,
    val heroCtaAgentic: String,

    // HomeTutorPro Case Study
    val htpEyebrow: String,
    val htpSubtitle: String,
    val htpDescription: String,
    val htpWhyTitle: String,
    val htpWhyText: String,
    val htpArchitecturePillarsTitle: String,
    val htpArchitectureHighlights: List<String>,
    val htpKeyFeaturesTitle: String,
    val htpKeyFeatures: List<String>,
    val htpScreenshotsTitle: String,
    val htpScreenshots: List<Pair<String, String>>,
    val htpCtaGithub: String,

    // Agentic Journey Section
    val agenticEyebrow: String,
    val agenticTitle: String,
    val agenticSubtitle: String,
    val agenticPillar1Title: String,
    val agenticPillar1Desc: String,
    val agenticPillar2Title: String,
    val agenticPillar2Desc: String,
    val agenticPillar3Title: String,
    val agenticPillar3Desc: String,
    val agenticPhilosophyTitle: String,
    val agenticPhilosophyText: String,
    val agenticBlogLink: String,

    // Experience Section
    val expEyebrow: String,
    val expTitle: String,
    val expSummary: String,
    val expCapabilitiesTitle: String,
    val expMilestones: List<MilestoneStrings>,

    // Blog Section
    val blogEyebrow: String,
    val blogTitle: String,
    val blogSubtitle: String,
    val blogReadArticle: String,
    val blogPosts: List<BlogPostStrings>,

    // Footer
    val footerCopyright: String
)

val EnglishStrings = AppStrings(
    navRole = "Senior Android & Tech Lead",
    navHome = "Home",
    navHomeTutorPro = "HomeTutorPro",
    navAgentic = "Agentic AI",
    navExperience = "Experience",
    navBlog = "Blog",
    languageToggleLabel = "ES",
    languageToggleTooltip = "Cambiar a Español",

    heroTitle = "Susana Córdoba — Senior Android Engineer & Tech Lead",
    heroTagline = "High-Impact Android Architecture & Agentic AI Systems",
    heroSubtitle = "7+ years of experience engineering robust mobile systems in high-demand environments (banking & consumer apps). Specialized in Kotlin, Jetpack Compose, Clean Architecture (MVVM/UDF), legacy modernization, and autonomous AI-driven workflows.",
    heroCtaHomeTutor = "Explore HomeTutorPro 📱",
    heroCtaAgentic = "My Agentic AI Journey 🤖",

    htpEyebrow = "FEATURED CASE STUDY",
    htpSubtitle = "Mobile Productivity for Private In-Home Tutors",
    htpDescription = "A production-grade Android application engineered with Clean Architecture and Jetpack Compose, designed to solve scheduling, finance, and student tracking for mobile tutors.",
    htpWhyTitle = "🧠 Why This Project Exists",
    htpWhyText = "HomeTutorPro is not a toy demo. It exists to model realistic, production-grade Android architecture, implement rigorous layered Clean Architecture with offline-first persistence, and solve actual everyday friction for mobile private tutors traveling between student homes.",
    htpArchitecturePillarsTitle = "🏗️ Architecture Pillars",
    htpArchitectureHighlights = listOf(
        "Layered Clean Architecture: Strict separation of concerns across Domain (pure Kotlin), Data, and Presentation with MVVM and UDF.",
        "Offline-First Design: Room database as the single source of truth with background synchronization.",
        "Type-Safe Navigation: Sealed hierarchy for type-safe route destinations across Compose screens.",
        "Agentic AI Verification: Local Gemini-powered audit orchestrator checking architecture rules, KDocs, security, and test skeletons."
    ),
    htpKeyFeaturesTitle = "✨ Key Mobile Features",
    htpKeyFeatures = listOf(
        "Student Profiles: Complete records with contact info, subjects, hourly rates, and notes.",
        "Dynamic Schedule: Daily and weekly agenda optimized for traveling tutors.",
        "Integrated Maps: Direct navigation to student addresses via Google Maps intents.",
        "Finance & Attendance: Lesson tracking, rates, payment status, and pending balances.",
        "Direct Communication: One-tap shortcuts for WhatsApp messaging and email.",
        "Resource Management: PDF and document organization organized per student."
    ),
    htpScreenshotsTitle = "📸 App Screenshots (Play Store Ready)",
    htpScreenshots = listOf(
        "/images/hometutor/play_store/01_dashboard.jpg" to "Dashboard & Overview",
        "/images/hometutor/play_store/03_sue_ia.jpg" to "Sue IA Assistant",
        "/images/hometutor/play_store/04_widget.jpg" to "Quick Access Widget",
        "/images/hometutor/play_store/05_alumnos.jpg" to "Student Directory",
        "/images/hometutor/play_store/06_perfil_gps.jpg" to "Profile & GPS Routes",
        "/images/hometutor/play_store/08_finanzas_cobros.jpg" to "Finances & Billing",
        "/images/hometutor/play_store/07_horario_vertical.jpg" to "Daily Schedule (Vertical)",
        "/images/hometutor/play_store/landscape_01_horario_panoramico.jpg" to "Weekly Calendar (Panoramic)"
    ),
    htpCtaGithub = "View Source Code on GitHub ⭐️",

    agenticEyebrow = "ENGINEERING METHODOLOGY",
    agenticTitle = "My Journey into Agentic Programming 🤖",
    agenticSubtitle = "How I transitioned from conventional Android development to designing specialized AI agents that enforce architecture rules, generate tests, and audit production code.",
    agenticPillar1Title = "1. Deterministic Safeguards",
    agenticPillar1Desc = "LLMs are probabilistic; software architecture cannot be. I established strict rules (AGENTS.md) and automated Gradle / Git diff scrapers so agents only act within well-defined boundaries.",
    agenticPillar2Title = "2. Multi-Agent Specialization",
    agenticPillar2Desc = "Rather than relying on one general prompt, I built 5 distinct sub-agents: Architecture Auditor, Security Scanner, Test Scaffolder, KDoc Specialist, and Refactor Assistant.",
    agenticPillar3Title = "3. Production Resilience",
    agenticPillar3Desc = "Real-world AI systems face rate limits (HTTP 429) and quota depletion. I engineered exponential backoff algorithms and developer loop mocks so work never stalls.",
    agenticPhilosophyTitle = "💡 Core Philosophy: 'Agents Propose, Compilers & Tests Dispose'",
    agenticPhilosophyText = "Agentic programming isn't about replacing developer judgment—it's about automating the cognitive burden of repetitive verification. By treating agent prompts as version-controlled code, our mobile architecture remains pristine even under rapid iteration.",
    agenticBlogLink = "Read my deep-dive article on Agentic Android Engineering →",

    expEyebrow = "TRACK RECORD & EXPERTISE",
    expTitle = "Professional Experience & Technical Leadership",
    expSummary = "Senior Android Engineer with 7+ years of experience building and maintaining production mobile applications in high-demand environments such as banking and consumer products. Specialized in modern Android architectures (Kotlin, Jetpack Compose, Clean Architecture) applied to real-world systems with a strong focus on legacy recovery, long-term maintainability, and delivery reliability. Experienced in contributing to architectural decisions, mentoring engineers, and engineering automated agentic workflows.",
    expCapabilitiesTitle = "🛠 Core Technical Capabilities",
    expMilestones = listOf(
        MilestoneStrings(
            role = "Software Engineer Mobile II",
            company = "Babel (Sevilla, España)",
            period = "2026 - Present",
            summary = "Developing native Android applications using Kotlin and Jetpack Compose following Clean Architecture principles.",
            highlights = listOf(
                "Develop Android applications using Kotlin and Jetpack Compose, following Clean Architecture principles.",
                "Collaborate with cross-functional teams to deliver high-quality features and optimize performance.",
                "Ensure code quality through testing, code reviews, and best development practices."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "Testing", "Coroutines")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer / Tech Lead",
            company = "Qaroní (Jerez de la Frontera, España)",
            period = "2024 - 2026",
            summary = "Technical leadership in complex Android applications, architectural decision-making, and legacy modernization.",
            highlights = listOf(
                "Technical leadership in complex Android applications and architectural decision-making.",
                "Recovery and stabilization of legacy projects through progressive refactoring and structural improvements.",
                "Implementation of Clean Architecture, MVVM, and state management in production systems.",
                "Mentoring junior and mid-level developers, improving code quality and review practices.",
                "Development of new features using Kotlin and Jetpack Compose in live products."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "MVVM", "Legacy Recovery", "Mentoring")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer",
            company = "Devoteam (Jerez de la Frontera, España)",
            period = "2023 - 2024",
            summary = "Native Android and Kotlin Multiplatform development across client and strategic product initiatives.",
            highlights = listOf(
                "Native Android development using Kotlin and Java across product and client projects.",
                "Implementation of Jetpack Compose and Kotlin Multiplatform in production apps.",
                "Application of Clean Architecture and modern reactive patterns in complex codebases.",
                "Work in agile, cross-functional teams delivering real-world mobile products."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "KMP", "Clean Architecture", "Java")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer",
            company = "NTT Data Europe & Latam (CaixaBankNow)",
            period = "2020 - 2023",
            summary = "Development and maintenance of large-scale banking mobile application (CaixaBankNow).",
            highlights = listOf(
                "Development and maintenance of large-scale banking mobile application (CaixaBankNow).",
                "Implementation of MVP, MVVM, and Clean Architecture in complex legacy systems.",
                "Integration with CI/CD pipelines using Jenkins, GitHub, and AppCenter.",
                "Technical mentoring of junior developers and stabilization of critical banking flows."
            ),
            technologies = listOf("Kotlin", "Java", "Banking Apps", "Clean Architecture", "CI/CD", "Jenkins")
        ),
        MilestoneStrings(
            role = "Mobile Developer (Android / iOS)",
            company = "Ecommerce Farm (Jerez de la Frontera, España)",
            period = "2019 - 2020",
            summary = "Development and maintenance of real-time sports betting applications for Android and iOS.",
            highlights = listOf(
                "Development and maintenance of a sports betting application for Android and iOS.",
                "Android stack: MVVM, Retrofit, Firebase, RxJava, real-time features.",
                "iOS development using Swift with MVC/MVVM patterns."
            ),
            technologies = listOf("Kotlin", "Swift", "Android", "iOS", "Firebase", "RxJava")
        ),
        MilestoneStrings(
            role = "Agentic Programming Pioneer & Creator",
            company = "HomeTutorPro & Independent Research",
            period = "2024 - Present",
            summary = "Designing autonomous AI audit suites, local Gemini orchestrators, and on-device Edge AI workflows.",
            highlights = listOf(
                "Engineered a local 5-agent audit orchestrator powered by Gemini models to validate Android code against strict architectural rules.",
                "Implemented resilient rate-limit handling with exponential backoff and test mocks for free-tier LLM integrations.",
                "Automated repetitive engineering tasks: KDoc autocompletion, Hilt module injection verification, and JUnit test generation."
            ),
            technologies = listOf("Gemini API", "Python", "Multi-Agent Systems", "Prompt Engineering", "CI Automation")
        )
    ),

    blogEyebrow = "ARTICLES & WRITING",
    blogTitle = "Engineering Blog & Technical Insights",
    blogSubtitle = "Deep dives into on-device AI architectures, local RAG on Android, RAM engineering, and autonomous agent workflows.",
    blogReadArticle = "Read Article →",
    blogPosts = listOf(
        BlogPostStrings(
            title = "Building HomeTutorPro: How I Mastered Agentic Programming",
            description = "A practical breakdown of designing a 5-agent audit suite for Android: handling rate limits, prompt engineering, and deterministic architectural safeguards.",
            date = "2026-10-02",
            readingTime = "8 min read",
            tags = listOf("Android", "Agentic Coding", "Kotlin", "Gemini"),
            slug = "building-home-tutor-pro"
        ),
        BlogPostStrings(
            title = "On-Device AI & Local RAG in Android: Running Gemma with MediaPipe",
            description = "Architectural blueprint for running a 100% offline, GDPR-compliant LLM on Android with zero API costs: local RAG over Room DB, INT4 quantization, and native GPU acceleration.",
            date = "2026-09-28",
            readingTime = "10 min read",
            tags = listOf("Android", "On-Device AI", "Gemma", "MediaPipe", "Local RAG"),
            slug = "on-device-ai-local-rag-android"
        ),
        BlogPostStrings(
            title = "The Hybrid Pattern in Mobile AI: Deterministic Fast-Path (<5ms) vs LLM",
            description = "Why sending everything to a language model is a major mobile anti-pattern, and how a two-tier hybrid architecture delivers 0ms conflict resolution and zero hallucinations.",
            date = "2026-09-15",
            readingTime = "7 min read",
            tags = listOf("Clean Architecture", "Kotlin", "Performance", "Edge AI"),
            slug = "hybrid-pattern-mobile-ai"
        ),
        BlogPostStrings(
            title = "RAM Engineering for Android 15-17: Running LLMs Without OOM Crashes",
            description = "Surviving strict per-app memory limits on modern Android: 16 KB page alignment, parallel warm-up during speech, and smart idle memory release.",
            date = "2026-08-20",
            readingTime = "9 min read",
            tags = listOf("Android 15-17", "Memory Management", "NDK", "MediaPipe"),
            slug = "android-memory-engineering-llm"
        )
    ),

    footerCopyright = "© 2026 Susana Córdoba Serrano • Built with Kotlin & Kobweb • Deployed with Firebase Hosting"
)

val SpanishStrings = AppStrings(
    navRole = "Senior Android & Tech Lead",
    navHome = "Inicio",
    navHomeTutorPro = "HomeTutorPro",
    navAgentic = "IA Agéntica",
    navExperience = "Experiencia",
    navBlog = "Blog",
    languageToggleLabel = "EN",
    languageToggleTooltip = "Switch to English",

    heroTitle = "Susana Córdoba — Senior Android Engineer & Tech Lead",
    heroTagline = "Arquitectura Android de Alto Impacto & Sistemas con IA Agéntica",
    heroSubtitle = "Más de 7 años de experiencia diseñando sistemas móviles robustos en entornos de alta exigencia (banca y aplicaciones de consumo masivo). Especializada en Kotlin, Jetpack Compose, Clean Architecture (MVVM/UDF), modernización de código legacy y desarrollo agéntico con IA.",
    heroCtaHomeTutor = "Ver HomeTutorPro 📱",
    heroCtaAgentic = "Mi Camino en IA Agéntica 🤖",

    htpEyebrow = "CASO DE ESTUDIO DESTACADO",
    htpSubtitle = "Productividad Móvil para Profesores Particulares a Domicilio",
    htpDescription = "Aplicación Android orientada a producción diseñada con Clean Architecture y Jetpack Compose, pensada para resolver la gestión de horarios, finanzas y seguimiento de alumnos para profesores itinerantes.",
    htpWhyTitle = "🧠 Por Qué Existe Este Proyecto",
    htpWhyText = "HomeTutorPro no es una maqueta de juguete. Nació para modelar una arquitectura Android real y escalable, implementar Clean Architecture rigurosa con persistencia offline-first y resolver los problemas diarios de los profesores que se desplazan continuamente entre domicilios.",
    htpArchitecturePillarsTitle = "🏗️ Pilares de Arquitectura",
    htpArchitectureHighlights = listOf(
        "Clean Architecture en 3 Capas: Separación estricta entre Dominio (Kotlin puro sin SDK de Android), Capa de Datos (Room/Offline-First) y Presentación reactiva con MVVM y UDF.",
        "Diseño Offline-First: Base de datos Room como única fuente de la verdad con sincronización en segundo plano.",
        "Navegación Type-Safe: Jerarquía sellada (sealed types) para rutas y destinos totalmente seguros en Jetpack Compose.",
        "Verificación Agéntica con IA: Orquestador local con Gemini para validar reglas arquitectónicas, KDocs, seguridad y esqueletos de test."
    ),
    htpKeyFeaturesTitle = "✨ Funcionalidades Clave en Móvil",
    htpKeyFeatures = listOf(
        "Fichas de Alumnos: Registro completo con datos de contacto, asignaturas, tarifa horaria y notas.",
        "Horario Dinámico: Agenda diaria y semanal optimizada para los trayectos del profesor.",
        "Mapas Integrados: Navegación directa al domicilio del alumno mediante intents de Google Maps.",
        "Finanzas y Asistencia: Control de clases impartidas, tarifas, estado de cobro y saldo pendiente.",
        "Comunicación Directa: Accesos directos para enviar mensajes por WhatsApp y correo en un solo toque.",
        "Gestión de Recursos: Organización de archivos PDF y material didáctico por estudiante."
    ),
    htpScreenshotsTitle = "📸 Capturas de la Aplicación (Play Store Ready)",
    htpScreenshots = listOf(
        "/images/hometutor/play_store/01_dashboard.jpg" to "Panel y Resumen",
        "/images/hometutor/play_store/03_sue_ia.jpg" to "Asistente Sue IA",
        "/images/hometutor/play_store/04_widget.jpg" to "Widget de Acceso Rápido",
        "/images/hometutor/play_store/05_alumnos.jpg" to "Fichas de Alumnos",
        "/images/hometutor/play_store/06_perfil_gps.jpg" to "Perfil y Rutas GPS",
        "/images/hometutor/play_store/08_finanzas_cobros.jpg" to "Finanzas y Cobros",
        "/images/hometutor/play_store/07_horario_vertical.jpg" to "Horario Diario (Vertical)",
        "/images/hometutor/play_store/landscape_01_horario_panoramico.jpg" to "Horario Semanal (Panorámico)"
    ),
    htpCtaGithub = "Ver Código Fuente en GitHub ⭐️",

    agenticEyebrow = "METODOLOGÍA DE INGENIERÍA",
    agenticTitle = "Mi Camino en la Programación Agéntica 🤖",
    agenticSubtitle = "Cómo pasé del desarrollo Android convencional a diseñar agentes de IA especializados que auditan reglas de arquitectura, generan tests y supervisan código en producción.",
    agenticPillar1Title = "1. Salvaguardas Deterministas",
    agenticPillar1Desc = "Los LLMs son probabilísticos; la arquitectura de software no puede serlo. Diseñé reglas estrictas (AGENTS.md) y extractores automatizados de Gradle y Git para que los agentes operen siempre bajo límites seguros y predecibles.",
    agenticPillar2Title = "2. Especialización Multi-Agente",
    agenticPillar2Desc = "En lugar de depender de un prompt genérico, creé 5 sub-agentes especializados: Auditor de Arquitectura, Escáner de Seguridad, Generador de Tests, Especialista en KDocs y Asistente de Refactorización.",
    agenticPillar3Title = "3. Resiliencia en Producción",
    agenticPillar3Desc = "Los sistemas de IA reales sufren límites de tasa (HTTP 429) y agotamiento de cuota. Desarrollé algoritmos con backoff exponencial y mocks en bucle de desarrollo para que el trabajo nunca se bloquee.",
    agenticPhilosophyTitle = "💡 Filosofía Fundamental: 'Los agentes proponen, el compilador y los tests disponen'",
    agenticPhilosophyText = "La programación agéntica no busca sustituir el criterio de la desarrolladora, sino liberar la carga cognitiva de la verificación repetitiva. Al tratar las instrucciones del agente como código versionado, la arquitectura de la app se mantiene impecable incluso con iteraciones rápidas.",
    agenticBlogLink = "Leer mi artículo en profundidad sobre Ingeniería Android Agéntica →",

    expEyebrow = "TRAYECTORIA Y ESPECIALIZACIÓN",
    expTitle = "Experiencia Profesional y Liderazgo Técnico",
    expSummary = "Senior Android Engineer con más de 7 años de experiencia construyendo y manteniendo aplicaciones móviles en producción dentro de entornos de alta exigencia como el sector bancario y productos de consumo. Especializada en arquitecturas Android modernas (Kotlin, Jetpack Compose, Clean Architecture) aplicadas a sistemas reales con un fuerte foco en recuperación de código legacy, mantenibilidad a largo plazo y fiabilidad en las entregas. Experiencia en toma de decisiones de arquitectura, mentoría técnica y flujos agénticos avanzados.",
    expCapabilitiesTitle = "🛠 Capacidades Técnicas Principales",
    expMilestones = listOf(
        MilestoneStrings(
            role = "Software Engineer Mobile II",
            company = "Babel (Sevilla, España)",
            period = "2026 - Presente",
            summary = "Desarrollo de aplicaciones Android nativas utilizando Kotlin y Jetpack Compose siguiendo principios de Clean Architecture.",
            highlights = listOf(
                "Desarrollo de aplicaciones Android nativas con Kotlin y Jetpack Compose bajo Clean Architecture.",
                "Colaboración con equipos multidisciplinares para entregar funcionalidades de alta calidad y optimizar rendimiento.",
                "Aseguramiento de calidad de código mediante testing, code reviews y mejores prácticas de ingeniería."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "Testing", "Coroutines")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer / Tech Lead",
            company = "Qaroní (Jerez de la Frontera, España)",
            period = "2024 - 2026",
            summary = "Liderazgo técnico en aplicaciones Android complejas, toma de decisiones de arquitectura y modernización de código legacy.",
            highlights = listOf(
                "Liderazgo técnico en aplicaciones Android complejas y toma de decisiones arquitectónicas.",
                "Recuperación y estabilización de proyectos legacy mediante refactorización progresiva y mejoras estructurales.",
                "Implementación de Clean Architecture, MVVM y gestión de estados en sistemas en producción.",
                "Mentoría a desarrolladores junior y mid-level, elevando prácticas de revisión y calidad de código.",
                "Desarrollo de nuevas funcionalidades con Kotlin y Jetpack Compose en productos activos."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "Clean Architecture", "MVVM", "Legacy Recovery", "Mentoring")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer",
            company = "Devoteam (Jerez de la Frontera, España)",
            period = "2023 - 2024",
            summary = "Desarrollo nativo en Android y Kotlin Multiplatform en proyectos de cliente y producto estratégico.",
            highlights = listOf(
                "Desarrollo nativo en Android con Kotlin y Java en proyectos de producto y clientes.",
                "Implementación de Jetpack Compose y Kotlin Multiplatform en aplicaciones en producción.",
                "Aplicación de Clean Architecture y patrones reactivos modernos en bases de código complejas.",
                "Trabajo en equipos ágiles multidisciplinares entregando productos móviles a escala real."
            ),
            technologies = listOf("Kotlin", "Jetpack Compose", "KMP", "Clean Architecture", "Java")
        ),
        MilestoneStrings(
            role = "Senior Android Engineer",
            company = "NTT Data Europe & Latam (CaixaBankNow)",
            period = "2020 - 2023",
            summary = "Desarrollo y mantenimiento de la aplicación bancaria de gran escala CaixaBankNow.",
            highlights = listOf(
                "Desarrollo y mantenimiento de aplicación bancaria móvil a gran escala (CaixaBankNow).",
                "Implementación de MVP, MVVM y Clean Architecture en sistemas legacy de alta complejidad.",
                "Integración con pipelines de CI/CD utilizando Jenkins, GitHub y AppCenter.",
                "Mentoría técnica a desarrolladores junior y estabilización de flujos bancarios críticos."
            ),
            technologies = listOf("Kotlin", "Java", "Banking Apps", "Clean Architecture", "CI/CD", "Jenkins")
        ),
        MilestoneStrings(
            role = "Mobile Developer (Android / iOS)",
            company = "Ecommerce Farm (Jerez de la Frontera, España)",
            period = "2019 - 2020",
            summary = "Desarrollo y mantenimiento de aplicación de apuestas deportivas en tiempo real para Android e iOS.",
            highlights = listOf(
                "Desarrollo y mantenimiento de aplicación de apuestas deportivas para Android e iOS.",
                "Stack Android: MVVM, Retrofit, Firebase, RxJava y funcionalidades en tiempo real.",
                "Desarrollo en iOS con Swift bajo patrones MVC/MVVM."
            ),
            technologies = listOf("Kotlin", "Swift", "Android", "iOS", "Firebase", "RxJava")
        ),
        MilestoneStrings(
            role = "Pionera en Programación Agéntica & Creadora",
            company = "HomeTutorPro e Investigación Independiente",
            period = "2024 - Presente",
            summary = "Diseño de herramientas personalizadas de orquestación multi-agente que automatizan auditorías arquitectónicas, revisiones de seguridad y scaffolding de tests.",
            highlights = listOf(
                "Diseñé un orquestador local de 5 agentes con modelos Gemini para validar código Android contra reglas estrictas de arquitectura.",
                "Implementé control resiliente de límites de cuota (HTTP 429) con backoff exponencial y mocks para integraciones con LLMs.",
                "Automaticé tareas rutinarias de ingeniería: autocompletado de KDocs, verificación de inyecciones Hilt y generación de tests JUnit."
            ),
            technologies = listOf("Gemini API", "Python", "Multi-Agent Systems", "Prompt Engineering", "CI Automation")
        )
    ),

    blogEyebrow = "ARTÍCULOS Y PUBLICACIONES",
    blogTitle = "Blog de Ingeniería y Notas Técnicas",
    blogSubtitle = "Inmersiones técnicas en arquitectura de IA on-device, RAG local en Android, ingeniería de memoria RAM y flujos de trabajo agénticos.",
    blogReadArticle = "Leer Artículo →",
    blogPosts = listOf(
        BlogPostStrings(
            title = "Construyendo HomeTutorPro: Cómo Dominé la Programación Agéntica",
            description = "Desglose práctico del diseño de una suite de 5 agentes para Android: control de cuotas, ingeniería de prompts y validación determinista de arquitectura.",
            date = "2026-10-02",
            readingTime = "8 min de lectura",
            tags = listOf("Android", "Agentic Coding", "Kotlin", "Gemini"),
            slug = "es/building-home-tutor-pro"
        ),
        BlogPostStrings(
            title = "IA On-Device y RAG Local en Android: Ejecutando Gemma con MediaPipe",
            description = "Arquitectura para ejecutar un LLM 100% offline y conforme al RGPD en Android con 0€ de coste en APIs: RAG sobre Room DB, cuantización INT4 y aceleración GPU nativa.",
            date = "2026-09-28",
            readingTime = "10 min de lectura",
            tags = listOf("Android", "On-Device AI", "Gemma", "MediaPipe", "Local RAG"),
            slug = "es/on-device-ai-local-rag-android"
        ),
        BlogPostStrings(
            title = "El Patrón Híbrido en IA Móvil: Fast-Path Determinista (<5ms) vs LLM",
            description = "Por qué enviar todo al modelo de lenguaje es un antipatrón en móvil, y cómo una arquitectura en dos niveles ofrece resolución de conflictos en 0ms y cero alucinaciones.",
            date = "2026-09-15",
            readingTime = "7 min de lectura",
            tags = listOf("Clean Architecture", "Kotlin", "Performance", "Edge AI"),
            slug = "es/hybrid-pattern-mobile-ai"
        ),
        BlogPostStrings(
            title = "Ingeniería de Memoria RAM en Android 15-17: Ejecutando LLMs sin OOM Crashes",
            description = "Cómo eludir los límites estrictos de memoria por app en Android moderno: alineación a páginas de 16 KB, calentamiento en paralelo durante la voz y descarga automática inteligente.",
            date = "2026-08-20",
            readingTime = "9 min de lectura",
            tags = listOf("Android 15-17", "Memory Management", "NDK", "MediaPipe"),
            slug = "es/android-memory-engineering-llm"
        )
    ),

    footerCopyright = "© 2026 Susana Córdoba Serrano • Desarrollado con Kotlin y Kobweb • Alojado en Firebase Hosting"
)

val LocalStrings = compositionLocalOf { EnglishStrings }

@Composable
fun rememberStrings(): AppStrings {
    val lang = LanguageState.currentLanguage
    return remember(lang) {
        when (lang) {
            AppLanguage.EN -> EnglishStrings
            AppLanguage.ES -> SpanishStrings
        }
    }
}
